import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import styles from "../../DualApproval.module.scss";
import type { BulkPaymentTopCardProps } from "./types";

export const useBulkAmount = (dualApproval: PaginatedDualApproval): string => {
  const { ach_payments_collect: achPaymentsCollectFeature } = useUserFeatures();
  if (achPaymentsCollectFeature) {
    return dualApproval.bulk?.netTotalDollarsWithSign || "$0";
  }
  return dualApproval.bulk?.netTotalDollarsWithoutSign || "$0";
};

const BulkPaymentTopCard = ({
  dualApproval,
  handleClickViewPayments,
}: BulkPaymentTopCardProps) => {
  const { l10n } = useLocalization();

  const bulkAmount = useBulkAmount(dualApproval);

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {dualApproval.bulk?.title}
        </h5>
        <h1 className="fontColor--primary">{bulkAmount}</h1>
        <hr className="margin--bottom--l margin--top--l" />

        {dualApproval.bulk?.paymentCreditCount &&
        dualApproval.bulk.paymentCreditCount > 0 ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-sending")}
            </h5>
            <div className="margin--bottom--m">
              {dualApproval.bulk.creditTotalDollars}{" "}
              {l10n.getString("via-payments-cta", {
                total: dualApproval.bulk.paymentCreditCount,
              })}
            </div>
          </>
        ) : null}

        {dualApproval.bulk?.paymentDebitCount &&
        dualApproval.bulk.paymentDebitCount > 0 ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-collecting")}
            </h5>
            <div className="margin--bottom--m">
              {dualApproval.bulk.debitTotalDollars}{" "}
              {l10n.getString("via-payments-cta", {
                total: dualApproval.bulk.paymentDebitCount,
              })}
            </div>
          </>
        ) : null}

        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-scheduled-date")}
        </h5>
        <div className="margin--bottom--m">
          {dualApproval.bulk?.longDateRange || dualApproval.bulk?.dateRange}
        </div>

        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-bulk-details")}
        </h5>
        <Button
          kind="plain"
          size="s"
          onClick={handleClickViewPayments}
          testId="view-payments"
        >
          {l10n.getString("view-payments-cta", {
            total: dualApproval.bulk?.paymentCount || 0,
          })}
          <span className="narmi-icon-chevron-right" />
        </Button>
        {dualApproval.bulk?.groupPosting ? (
          <div className="fontSize--xs fontColor--primary">
            {l10n.getString("group-posting-copy")}
          </div>
        ) : null}
      </ContentCard>
    </div>
  );
};

export default BulkPaymentTopCard;
